<template>
    <KCard
        :as="as"
        :class="classes"
    >
        <span
            v-if="$slots.icon"
            :class="element('icon')"
            aria-hidden="true"
        >
            <slot name="icon" />
        </span>

        <component
            :is="titleAs"
            v-if="$slots.title || title"
            class="k-heading-4"
            :class="[element('title')]"
        >
            <slot name="title">
                {{ title }}
            </slot>
        </component>

        <div
            v-if="$slots.default || text"
            class="k-text k-prose"
            :class="[element('text')]"
        >
            <slot>{{ text }}</slot>
        </div>

        <div
            v-if="hasFooter"
            :class="element('footer')"
        >
            <KEyebrow
                v-if="$slots.meta || meta"
                :class="element('meta')"
            >
                <slot name="meta">
                    {{ meta }}
                </slot>
            </KEyebrow>

            <a
                v-if="hasLink"
                :href="href"
                :class="element('link')"
            >
                <slot name="link">
                    {{ linkLabel }}
                    <svg
                        :class="element('arrow')"
                        viewBox="0 0 16 16"
                        fill="none"
                        aria-hidden="true"
                        focusable="false"
                    >
                        <path
                            d="M2.5 8h10M9 4.5l3.5 3.5L9 11.5"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </slot>
            </a>
        </div>
    </KCard>
</template>

<script setup lang="ts">
import type { KServiceCardProps } from './types'
import useBem from '@yeeroen/use-bem'

import { computed, useSlots } from 'vue'
import KCard from '../card/k-card.vue'

import KEyebrow from '../eyebrow/k-eyebrow.vue'

defineOptions({ name: 'KServiceCard' })

const props = withDefaults(defineProps<KServiceCardProps>(), {
    as: 'article',
    titleAs: 'h3',
})

defineSlots<{
    default?: () => unknown
    icon?: () => unknown
    title?: () => unknown
    meta?: () => unknown
    link?: () => unknown
}>()

const slots = useSlots()

const { block, element, modifier } = useBem('k-service-card')

const hasLink = computed(() => Boolean(props.href && (props.linkLabel || slots.link)))
const hasFooter = computed(() => Boolean(props.meta || slots.meta || hasLink.value))

const classes = computed(() => block(
    modifier('linked', hasLink.value),
))
</script>

<style lang="scss">
@use './k-service-card.scss';
</style>
